// src/hooks/useEvidences.js
// AÇÃO 4 F8: hook para listar evidências IA pendentes e aplicar decisões
// humanas via edge function `apply-evidence`.

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// Helper: extrai UUID de um suggested_value/previous_value JSONB que armazena
// oferta_id (em produção é string JSON ou objeto com .value).
function asUuid(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && typeof v.value === 'string') return v.value;
  return null;
}

export function useEvidences() {
  const [evidences, setEvidences] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [emailMap, setEmailMap] = useState({});
  const [ofertaMap, setOfertaMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Query simples + enriquecimento manual com nome da empresa.
      // (Evita depender de FK nomeado entre ai_evidence.entity_id e empresas.id,
      // que entity_id é polimórfico por entity_type.)
      const { data: rows, error: qErr } = await supabase
        .from('ai_evidence')
        .select('*')
        .eq('human_decision', 'pending')
        .order('confidence', { ascending: false })
        .order('created_at', { ascending: true });
      if (qErr) throw qErr;

      const list = rows || [];

      // 1) empresas (entity_type='company') — mantido
      const empresaIds = [...new Set(
        list.filter(e => e.entity_type === 'company').map(e => e.entity_id),
      )];

      // 2) emails (entity_type='email')
      const emailIds = [...new Set(
        list.filter(e => e.entity_type === 'email').map(e => e.entity_id).filter(Boolean),
      )];

      // 3) ofertas (suggested_value + previous_value quando field_name='oferta_id')
      const ofertaIds = [...new Set(
        list
          .filter(e => e.field_name === 'oferta_id')
          .flatMap(e => [asUuid(e.suggested_value), asUuid(e.previous_value)])
          .filter(Boolean),
      )];

      const [empResp, emailResp, ofertaResp] = await Promise.all([
        empresaIds.length
          ? supabase.from('empresas').select('id, company_name, empresa').in('id', empresaIds)
          : Promise.resolve({ data: [] }),
        emailIds.length
          ? supabase
              .from('emails_gmail')
              .select('id, subject, from_email, data_envio, direcao')
              .in('id', emailIds)
          : Promise.resolve({ data: [] }),
        ofertaIds.length
          ? supabase
              .from('ofertas')
              .select('id, nome, status, empresa_id, empresas:empresa_id (company_name, empresa)')
              .in('id', ofertaIds)
          : Promise.resolve({ data: [] }),
      ]);

      if (empResp.error)    throw empResp.error;
      if (emailResp.error)  throw emailResp.error;
      if (ofertaResp.error) throw ofertaResp.error;

      const empresaMap = Object.fromEntries((empResp.data || []).map(e => [e.id, e]));
      const emailIdx   = Object.fromEntries((emailResp.data || []).map(e => [e.id, e]));
      const ofertaIdx  = Object.fromEntries((ofertaResp.data || []).map(o => [o.id, o]));

      const enriched = list.map(ev => ({
        ...ev,
        empresa: ev.entity_type === 'company' ? empresaMap[ev.entity_id] || null : null,
      }));

      setEvidences(enriched);
      setPendingCount(enriched.length);
      setEmailMap(emailIdx);
      setOfertaMap(ofertaIdx);
    } catch (e) {
      console.error('[useEvidences] loadPending error:', e);
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const decide = useCallback(async ({
    evidence_id,
    decision,
    decided_by,
    decision_reason,
    override_value,
  }) => {
    setLoading(true);
    setError(null);
    try {
      const body = { evidence_id, decision, decided_by };
      if (decision_reason) body.decision_reason = decision_reason;
      if (override_value !== undefined) body.override_value = override_value;

      const { data, error: fnErr } = await supabase.functions.invoke('apply-evidence', { body });
      if (fnErr) throw fnErr;
      if (data?.error || data?.ok === false) {
        throw new Error(data?.error || 'Falha ao aplicar decisão');
      }
      await loadPending();
      return data;
    } catch (e) {
      console.error('[useEvidences] decide error:', e);
      setError(e?.message ?? String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadPending]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  return { evidences, pendingCount, emailMap, ofertaMap, loading, error, loadPending, decide };
}
